import * as _ from 'lodash';
import { useState } from 'react';
import type { ReactNode, VoidFunctionComponent } from 'react';

import { useContributorSnapshot } from '../../hooks/api/contributors/useContributorSnapshot';
import Button from '../generic/Button/Button';
import Card from '../generic/Card/Card';
import Flex from '../generic/Flex';
import Link from '../generic/Link/Link';
import List from '../generic/List';
import Loader from '../generic/Loader';

const ContributorsCard: VoidFunctionComponent = () => {
  const [isCopied, setIsCopied] = useState(false);
  const { data, isLoading } = useContributorSnapshot();

  const handleCopyToClipboard = async (): Promise<void> => {
    const contributorInfo = [
      `Last snapshot: ${data?.snapshot || 'n/a'}`,
      `Count (Last 90 days): ${_.isNumber(data?.activeContributorCountLast90days) ? data?.activeContributorCountLast90days : 'n/a'}`,
      `Count (Last 365 days): ${_.isNumber(data?.activeContributorCountLast365days) ? data?.activeContributorCountLast365days : 'n/a'}`,
    ].join('\n');

    await navigator.clipboard.writeText(contributorInfo);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  let content: ReactNode;
  if (isLoading) {
    content = <Loader />;
  } else if (!data?.snapshot) {
    content = (
      <div className="flex-table text-center margin-top-lg">
        No snapshot data available.
      </div>
    );
  } else {
    content = (
      <>
        <List isBorderless>
          <List.Body hasPadding={false}>
            <List.Row
              hasTightPadding
              hasTightVerticalPadding
              left={<b>Last snapshot</b>}
              right={data.snapshot || 'n/a'}
            />
            <List.Row
              hasTightPadding
              hasTightVerticalPadding
              left={<b>Count (Last 90 days)</b>}
              right={_.isNumber(data.activeContributorCountLast90days) ? data.activeContributorCountLast90days : 'n/a'}
            />
            <List.Row
              hasTightPadding
              hasTightVerticalPadding
              left={<b>Count (Last 365 days)</b>}
              right={_.isNumber(data.activeContributorCountLast365days) ? data.activeContributorCountLast365days : 'n/a'}
            />
          </List.Body>
        </List>
        <p>Note: Snapshots are generated every Sunday morning.</p>
      </>
    );
  }

  return (
    <Card isFullWidth padding="medium">
      <Card.Header>
        <Flex direction="row" align="center" justify="space-between">
          <div>Tracked Contributors</div>
          <Button onClick={handleCopyToClipboard} size="micro" type="outline">
            {isCopied ? 'Copied!' : 'Copy'}
          </Button>
        </Flex>
      </Card.Header>
      <Card.Content>
        {content}
        <div>
          <Link to="/api/contributors/count" openInNewTab>
            Click here to get the latest (as of now) contributor count for the last 365 days
          </Link>
        </div>
      </Card.Content>
    </Card>
  );
};

export default ContributorsCard;
